import React from 'react'
import { render, screen } from '@testing-library/react'
import { MemoryRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import '@testing-library/jest-dom'

// Both buttons gate on `isInternal` alone. The trap is `hasFullAdminAccess`,
// which is also true for ORGANIZATION_OWNER - so the ORG_OWNER fixture below
// carries it, and swapping either gate to it fails these tests.

const mockUseAdminAccess = jest.fn()

jest.mock('../../services/hooks/useAdminAccess', () => ({
  __esModule: true,
  default: (...args) => mockUseAdminAccess(...args),
}))

jest.mock('../../Contexts/APIContext', () => ({
  useAPI: () => ({ startIngestRequest: jest.fn() }),
}))

import AddArtistButton from './AddArtistButton'
import RefreshArtistButton, { artistChannelId } from './RefreshArtistButton'

const INTERNAL = { isInternal: true, isAdmin: false, hasAdminAccess: false, hasFullAdminAccess: true }
/** An ORGANIZATION_OWNER: full admin access, zero internal permissions. */
const ORG_OWNER = { isInternal: false, isAdmin: false, hasAdminAccess: true, hasFullAdminAccess: true }
const PLAIN_USER = { isInternal: false, isAdmin: false, hasAdminAccess: false, hasFullAdminAccess: false }

const ARTIST = {
  name: 'Sabrina Carpenter',
  social_media: [{ f_id: 'youtube', url: 'https://www.youtube.com/channel/UCy9EQSb0ZVgrP2xhBpCvKvQ' }],
}

function draw(ui) {
  const client = new QueryClient({ defaultOptions: { queries: { retry: false } } })
  return render(
    <QueryClientProvider client={client}>
      <MemoryRouter>{ui}</MemoryRouter>
    </QueryClientProvider>
  )
}

beforeEach(() => {
  jest.clearAllMocks()
  mockUseAdminAccess.mockReturnValue(INTERNAL)
})

const BUTTONS = [
  ['AddArtistButton', <AddArtistButton key="a" />, 'ADD ARTIST'],
  ['RefreshArtistButton', <RefreshArtistButton key="r" artist={ARTIST} />, 'REQUEST SYNC'],
]

describe('internal-staff-only gating', () => {
  BUTTONS.forEach(([name, ui, label]) => {
    it(`${name} renders for internal Feels staff`, () => {
      mockUseAdminAccess.mockReturnValue(INTERNAL)
      draw(ui)
      expect(screen.getByRole('button', { name: label })).toBeInTheDocument()
    })

    it(`${name} renders NOTHING for an organization owner, whose hasFullAdminAccess is true`, () => {
      mockUseAdminAccess.mockReturnValue(ORG_OWNER)
      const { container } = draw(ui)
      expect(container).toBeEmptyDOMElement()
    })

    it(`${name} renders NOTHING for an ordinary user`, () => {
      mockUseAdminAccess.mockReturnValue(PLAIN_USER)
      const { container } = draw(ui)
      expect(container).toBeEmptyDOMElement()
    })
  })
})

describe('the artist page button needs a channel it can actually send', () => {
  it('reads the channel id out of the artist YouTube social link', () => {
    expect(artistChannelId(ARTIST)).toBe('UCy9EQSb0ZVgrP2xhBpCvKvQ')
  })

  it('does not render when the YouTube link is a handle, which the service refuses', () => {
    // Better to not draw the button than to draw one that always fails.
    const handleOnly = {
      name: 'Sabrina Carpenter',
      social_media: [{ f_id: 'youtube', url: 'https://www.youtube.com/@sabrinacarpenter' }],
    }
    expect(artistChannelId(handleOnly)).toBeNull()
    const { container } = draw(<RefreshArtistButton artist={handleOnly} />)
    expect(container).toBeEmptyDOMElement()
  })

  it('does not render for an artist with no YouTube link at all', () => {
    expect(artistChannelId({ name: 'Nobody', social_media: [] })).toBeNull()
    expect(artistChannelId({})).toBeNull()
    expect(artistChannelId(null)).toBeNull()
    const { container } = draw(<RefreshArtistButton artist={{ name: 'Nobody' }} />)
    expect(container).toBeEmptyDOMElement()
  })
})
